/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

// material
import {
    Page,
    Content,
    ListTable,
    FilterMenu,
    Link,
    displayModal,
    SearchBar,
    GridSet,
    GridItem, Grid
} from '@insticator/insticator-ui';
import PageHeader from '../../../../material/structure/pageheader/pageheader';
import PageContent from '../../../../material/structure/pagecontent/pagecontent';

// styles
import injectSheet from 'react-jss';
import styles from './mcm_styles';

// Others
import {fetchChildPublishersData, fetchMcmAccountsData} from "../../../../state/thunks/mcm";
import { defaultCols, accountStatusMap, delegationMap, filters, defaultFilterObject } from './constants';
import McmModal from "./mcm_modal";

/* ========== ~~~~~~~~~~ GLOBAL SETTINGS - MCM PAGE ~~~~~~~~~~ ========== */

const MCMPage = injectSheet(styles)((props) => {

    const dispatch = useDispatch();
    const [columns, setColumns] = useState(defaultCols);
    const [tableData, setTableData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const childPublishers = useSelector((state) => state.mcm.childPublishers);
    const loading = useSelector((state) => state.mcm.loading);
    const mcmAccounts = useSelector((state) => state.mcm.mcmAccounts);
    const [filterObject, setFilterObject] = useState(defaultFilterObject);
    const [searchCategory, setSearchCategory] = useState({"name":"Child publisher","value":"childPublisher"});

    useEffect(() => {
        dispatch(fetchMcmAccountsData());
    }, []);

    useEffect(() => {
        dispatch(fetchChildPublishersData());
    }, [mcmAccounts]);

    useEffect(() => {
        transformChildPublisherData();
    }, [childPublishers]);

    const transformChildPublisherData = () => {
        let tableDataArr = [];
        setTableData([]);
        childPublishers.forEach((item)=> {
            let tableRow = {};
            tableRow.childPublisher = item.name;
            tableRow.parentPublisher = item.parentNetworkId;
            tableRow.sellerId = item.sellerId;
            tableRow.childNetworkCode = item.childNetworkCode;
            tableRow.status = item.accountStatus in accountStatusMap ? accountStatusMap[item.accountStatus] : "-";
            tableRow.delegation = item.approvedDelegationType in delegationMap ? delegationMap[item.approvedDelegationType] : "-";
            if(item.overrideGamRevenueShare!=null && !isNaN(item.overrideGamRevenueShare)) {
                let parentShare = item.overrideGamRevenueShare * 100;
                let childShare = 100 - parentShare;
                tableRow.revenueShare = parentShare + "% / " + childShare + "%";
                tableRow.overrideGamRevenueShare = item.overrideGamRevenueShare;
                tableRow.gamRevenueShare = item.gamRevenueShare;
            } else if(item.gamRevenueShare!=null && !isNaN(item.gamRevenueShare)) {
                let parentShare = item.gamRevenueShare * 100;
                let childShare = 100 - parentShare;
                tableRow.revenueShare = parentShare + "% / " + childShare + "%";
                tableRow.gamRevenueShare = item.gamRevenueShare;
            } else {
                tableRow.revenueShare = '-';
            }
            tableRow.insticatorAccountUUID = item.insticatorAccountUUID;
            tableRow.insticatorAccountDisplayName = item.insticatorAccountDisplayName;
            tableDataArr.push(tableRow);
        });
        setTableData(tableDataArr);
        filter(filterObject, tableDataArr);
    }

    const filter = function(filterObj, tableDataInput) {
        setFilterObject(filterObj);
        let input = tableDataInput ? tableDataInput : tableData;
        let filteredDataObj = input.filter(
            function(item){
                if(filterObj.delegation.length > 0 && filterObj.delegation.indexOf(item.delegation) === -1)
                    return false;
                if(filterObj.linked.length > 0 && filterObj.linked.includes('yes') && !item.insticatorAccountUUID)
                    return false;
                if(filterObj.linked.length > 0 && filterObj.linked.includes('no') && item.insticatorAccountUUID)
                    return false;
                if(filterObj.status.length > 0 && filterObj.status.indexOf(item.status) === -1)
                    return false;
                return true;
            }
        );
        let formattedFilteredData = filteredDataObj.map((element, index) => {
            return {
                childPublisher: (
                    <Link
                        type={`action`}
                        action={() => openModal(element)}
                        operation={'link'}
                        text={<span className={props.classes.CustomLink}>{element.childPublisher}</span>}
                        color={`primary`} />
                ),
                parentPublisher: element.parentPublisher,
                sellerId: element.sellerId,
                childNetworkCode: element.childNetworkCode,
                status: element.status,
                delegation: element.delegation,
                revenueShare: element.revenueShare
            }
        });
        setFilteredData(formattedFilteredData);
    }

    const openModal = function(childPublisher) {
        dispatch(displayModal(true, <McmModal childPublisher={childPublisher} mcmAccounts={mcmAccounts} />));
    }

    const onSearch = function(keyword) {
        if(keyword) {
            let searchResult = filteredData.filter((item)=>{
                switch (searchCategory.value) {
                    case "childPublisher":
                        if(item.childPublisher.props.text.props.children)
                            return item.childPublisher.props.text.props.children.toLowerCase().includes(keyword.toLowerCase());
                        else
                            return false;
                    case "parentPublisher":
                        if(item.parentPublisher)
                            return item.parentPublisher.includes(keyword);
                        else
                            return false;
                    case "networkCode":
                        if(item.childNetworkCode)
                            return item.childNetworkCode.includes(keyword);
                        else
                            return false;
                }
            });
            setFilteredData(searchResult);
        } else {
            filter(filterObject, tableData);
        }
    }

    return (
        <Page name={'mcmPage'}>
            <Content>
                <PageHeader title={'Global Settings'} subtitle={'MCM'} />
                <PageContent>
                    <Grid>
                        <div className={props.classes.Controls}>
                            <GridSet direction={`horizontal`}>
                                <GridItem>
                                    <div className={props.classes.FilterMenuCustom}>
                                        <FilterMenu name={'filtermenu'}
                                                    filters={filters}
                                                    onApply={filter}
                                        />
                                    </div>
                                </GridItem>
                                <GridItem>
                                    <div className={props.classes.SearchBar}>
                                        <SearchBar type={'search'}
                                                   placeholder={'Search...'}
                                                   onChange={onSearch}
                                                   categories={[
                                                       {"name":"Child publisher","value":"childPublisher"},
                                                       {"name":"Parent publisher","value":"parentPublisher"},
                                                       {"name":"Network code","value":"networkCode"}
                                                   ]}
                                                   selectCategory={function(item){setSearchCategory(item)}}
                                                   currentCategory={searchCategory}
                                        />
                                    </div>
                                </GridItem>
                            </GridSet>
                        </div>
                    </Grid>
                    <br></br>
                    <div className={props.classes.ListTableCustom}>
                        <ListTable name={`mcmTable`}
                                   size={`full`}
                                   columnHeads={columns}
                                   data={filteredData}
                                   rowsPerPage={10}
                                   paginationLocation={`bottom`}
                                   loading={loading}
                        />
                    </div>
                </PageContent>
            </Content>
        </Page>
    );
});

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default MCMPage;